import moment from "moment/min/moment-with-locales";
import React, { useEffect, useMemo, useState } from "react";
import { Col, Form, ToggleButton, ToggleButtonGroup } from "react-bootstrap";
import { useLoaderData } from "react-router-dom";
import DataDescription from "../components/DataDescription";
import EmptyRow from "../components/EmptyRow";
import { getTrendLabel } from "../components/HelperFunctions";
import { useFetchData } from "../components/HelperHooks";
import PageDesciption from "../components/PageDesciption";
import DatesPicker from "../components/Pickers/DatesPicker";
import HybridHitlistRegionPicker from "../components/Pickers/HybridHitlistRegionPicker";
import { useUserFormState } from "../components/UserSelection";
import HybridHitlistHeader from "../components/hybridHitlist/HybridHitlistHeader";
import HybridHitlistRow from "../components/hybridHitlist/HybridHitlistRow";
import { DataContent, InputSelection, ModulePage, RATable, SearchWithDownloadRow, SoleDescriptionRow } from "../components/pageSections";
import { useSongCatalogueModal } from "../components/songCatalogue/SongCatalogueModal";
import useSortableData from "../components/useSortableData";

const extraDefaults = { region: [], airplayMix: true };

export default function HybridHitlist() {
    const moduleName = "hybridHitlist";
    const { latestData } = useLoaderData();
    const [latestCalculatedDate, setLatestCalculatedDate] = useState(null);

    const [formData, setFormData, saveFormData, loadFormData] = useUserFormState(moduleName, extraDefaults);
    const [loading, errorCode, data, fetchData] = useFetchData();

    const [filterData, setFilterData] = useState({ searchString: "" });
    const [submittedFormData, setSubmittedFormData] = useState(null);

    const { setCatalogueSong, SongCatalogueModalComponent } = useSongCatalogueModal();

    useEffect(() => {
        async function setLatestDate() {
            const resolvedDates = (await latestData).data;
            const selectedRegionDate = resolvedDates.find(
                x => x.mixed === formData.airplayMix && x.job_id === (formData.airplayMix ? formData.region[0].mix_id : formData.region[0].digital_id)
            );
            const baseDate = moment(selectedRegionDate.latest_date);
            setLatestCalculatedDate(baseDate.clone().endOf("isoWeek"));
            const defaultRange = [baseDate.clone().toDate(), baseDate.clone().endOf("isoWeek").toDate()];
            if (formData.dateRange === undefined || (formData.dateRange && formData.dateRange[1] > defaultRange[1])) {
                setFormData(current => ({ ...current, dateRange: defaultRange }));
            }
        }
        if (latestData && formData.region.length) setLatestDate();
        else setLatestCalculatedDate(null);
    }, [formData.airplayMix, formData.dateRange, formData.region, latestData, setFormData]);

    async function handleFormSubmit(event) {
        event.preventDefault();
        await saveFormData();
        await saveFormData(formData.region[0].radio_id);
        const id = formData.airplayMix ? formData.region[0].mix_id : formData.region[0].digital_id;
        const hitlistType = formData.airplayMix ? "mixed_hitlist" : "digital_hitlist";
        const basis_week = moment(formData.dateRange[0]).format("YYYY-MM-DD");
        const url = `/hybrid_hitlist/${hitlistType}/${id}/${basis_week}`;
        await fetchData(url);
        setSubmittedFormData(formData);
    }

    const songsMemo = useMemo(() => {
        if (data) {
            let filteredData = data;
            if (filterData.searchString) {
                filteredData = filteredData.filter(
                    x =>
                        x.song.artist.toLowerCase().includes(filterData.searchString.toLowerCase().trim()) ||
                        x.song.title.toLowerCase().includes(filterData.searchString.toLowerCase().trim()) ||
                        x.song.release_year?.toString().includes(filterData.searchString.trim())
                );
            }
            return filteredData;
        }
    }, [data, filterData.searchString]);

    const { sortedItems, requestSort, sortConfig } = useSortableData(songsMemo);

    const csvHeaders = useMemo(
        () =>
            formData.airplayMix
                ? ["Rank", "Artist", "Title", "Release Year", "Digital Rank", "Airplay Rank", "Previous Rank", "Trend"]
                : ["Rank", "Artist", "Title", "Release Year", "Trend"],
        [formData.airplayMix]
    );

    const csvData = useMemo(
        () =>
            formData.airplayMix
                ? sortedItems?.map(x => [
                      x.rank,
                      x.song.artist,
                      x.song.title,
                      x.song.release_year,
                      x.digital_rank,
                      x.airplay_rank,
                      x.previous_rank,
                      getTrendLabel(x.trend),
                  ])
                : sortedItems?.map(x => [x.rank, x.song.artist, x.song.title, x.song.release_year, getTrendLabel(x.trend)]),
        [formData.airplayMix, sortedItems]
    );

    return (
        <ModulePage>
            <ModulePage.Title>Hybrid Hitlist</ModulePage.Title>
            <ModulePage.InputSelection handleFormSubmit={handleFormSubmit} disabled={!formData.region.length || !formData.dateRange}>
                <InputSelection.Main>
                    <HybridHitlistRegionPicker formData={formData} setFormData={setFormData} name={moduleName} loadOnChange={loadFormData} />
                    <DatesPicker
                        formData={formData}
                        setFormData={setFormData}
                        latestAvailableDate={latestCalculatedDate}
                        disabled={!formData.region.length}
                        predefinedRanges={[]}
                        hoverRange={"week"}
                        showWeekNumbers
                        oneTap
                    />
                    <Form.Group as={Col} className="d-flex flex-column col-12 col-xl-3">
                        <Form.Label>Mix with airplay</Form.Label>
                        <ToggleButtonGroup
                            type="radio"
                            name="airplayMix"
                            value={formData.airplayMix}
                            onChange={value => setFormData({ ...formData, airplayMix: value })}
                            className=" ra-switcher">
                            <ToggleButton id={`hybrid-mixed`} variant={"ra-dark-blue-outline"} value={true}>
                                Yes
                            </ToggleButton>
                            <ToggleButton id={`hybrid-digital`} variant={"ra-dark-blue-outline"} value={false}>
                                No
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Form.Group>
                </InputSelection.Main>
            </ModulePage.InputSelection>
            <ModulePage.DataContent loading={loading} errorCode={errorCode}>
                <DataContent.Placeholder show={!data}>
                    <PageDesciption
                        title={"Hybrid Hitlist"}
                        whatList={[
                            "Get a “digital” airplay chart, showing what music is being listened to across a large panel of online sources",
                            "Mix your “digital” airplay results with your regional market airplay to get an early warning chart for upcoming hits",
                            "Choose from a number of different countries and formats",
                        ]}
                        knowList={[
                            {
                                text: "Hybrid Hitlist runs every week, and displays the most recent week of data",
                            },
                            { text: "Sources are Apple Music, Spotify, Youtube, Deezer, Shazam, and more" },
                            { text: "Open the song catalogue by clicking on the row of a song" },
                        ]}
                        videoSrc="https://www.youtube.com/embed/O1UzpcRKUWU?si=XL8L-nYuRDynFE_E"
                    />
                </DataContent.Placeholder>
                {data && (
                    <>
                        <SoleDescriptionRow>
                            <DataDescription>
                                <DataDescription.Simple label="Region" value={submittedFormData?.region[0].name} />
                                <DataDescription.Simple label="Type" value={submittedFormData?.airplayMix ? "Mixed" : "Digital"} />
                                <DataDescription.DateRange startDate={submittedFormData?.dateRange[0]} />
                            </DataDescription>
                        </SoleDescriptionRow>
                        <SearchWithDownloadRow
                            filterData={filterData}
                            setFilterData={setFilterData}
                            data={csvData}
                            csvHeaders={csvHeaders}
                            filename={`Hybrid Hitlist ${submittedFormData?.region[0].name}`}
                        />
                        <RATable>
                            <RATable.Header>
                                <HybridHitlistHeader requestSort={requestSort} sortConfig={sortConfig} airplayMix={submittedFormData?.airplayMix} />
                            </RATable.Header>
                            <RATable.Body>
                                {!sortedItems?.length ? (
                                    <EmptyRow text="No songs found" />
                                ) : (
                                    sortedItems.map(x => (
                                        <HybridHitlistRow
                                            key={x.song.item_id}
                                            item={x}
                                            setCatalogueSong={setCatalogueSong}
                                            airplayMix={submittedFormData?.airplayMix}
                                        />
                                    ))
                                )}
                            </RATable.Body>
                        </RATable>
                    </>
                )}
                <SongCatalogueModalComponent />
            </ModulePage.DataContent>
        </ModulePage>
    );
}
